import React, { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { Eye } from 'phosphor-react'
import {
    TICKET_PRIORITY,
    TICKET_STATUS,
    formatLocation,
    formatTicketAssigneeLabel,
    formatTicketDateTime,
    getAssigneeTypeLabel,
    getInitials,
    isAnswerNegative,
} from './utils'
import { TicketStatusPill } from 'page-components/auto-ticket-rules/styles'
import { PreviewEyeButton } from 'page-components/task/dashboard/task-dashboard-styles'

const statusStyle = statusId => {
    const meta = TICKET_STATUS[statusId] || TICKET_STATUS[1]
    const map = {
        open: { bg: '#f1f5f9', color: '#475569' },
        ack: { bg: '#dbeafe', color: '#1d4ed8' },
        progress: { bg: '#fef3c7', color: '#b45309' },
        resolved: { bg: '#dcfce7', color: '#15803d' },
        closed: { bg: '#f4f4f5', color: '#52525b' },
        escalated: { bg: '#ffedd5', color: '#c2410c' },
        hold: { bg: '#f3e8ff', color: '#6d28d9' },
        pending: { bg: '#e0e7ff', color: '#3730a3' },
        cancelled: { bg: '#f4f4f5', color: '#52525b' },
    }
    return map[meta.pill] || map.open
}

const QuickPreview = styled.div`
    position: fixed;
    width: 420px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.18);
    z-index: 900;
    max-height: 75vh;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`

const PreviewHeader = styled.header`
    padding: 14px 16px;
    border-bottom: 1px solid #f3f4f6;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;

    h2 {
        font-size: 14px;
        margin: 0 0 2px;
        line-height: 1.35;
    }

    .sub {
        color: #6b7280;
        font-size: 12px;
    }
`

const SmallButton = styled.button`
    background: ${p => (p.$active ? '#2563eb' : '#f3f4f6')};
    color: ${p => (p.$active ? '#fff' : '#374151')};
    border: 0;
    border-radius: 6px;
    padding: 4px 8px;
    font-size: 12px;
    cursor: pointer;
    font-family: inherit;
`

const PreviewBody = styled.div`
    overflow: auto;
    padding: 12px 16px 4px;
`

const SectionLabel = styled.div`
    margin: 10px 0 6px;
    color: #6b7280;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;

    &:first-child {
        margin-top: 0;
    }
`

const BadgeRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 8px;
`

const MetaGrid = styled.div`
    display: grid;
    grid-template-columns: 96px 1fr;
    gap: 8px 10px;
    font-size: 13px;

    .k {
        color: #6b7280;
    }

    .v {
        color: #111827;
        min-width: 0;
        word-break: break-word;
    }
`

const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 6px;
    padding: 1px 6px;
    border-radius: 999px;
    background: ${p => (p.$danger ? '#fee2e2' : '#f4f4f5')};
    color: ${p => (p.$danger ? '#b91c1c' : '#52525b')};
    font-size: 10px;
    font-weight: 700;
`

const LoadingText = styled.p`
    margin: 8px 0 16px;
    color: #6b7280;
    font-size: 13px;
`

const Avatar = styled.span`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #dbeafe;
    color: #1d4ed8;
    font-size: 10px;
    font-weight: 700;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`

const PreviewFooter = styled.div`
    padding: 12px 16px;
    border-top: 1px solid #f3f4f6;
`

const ViewButton = styled.button`
    width: 100%;
    background: #2563eb;
    color: #fff;
    border: 0;
    border-radius: 6px;
    padding: 10px 12px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    font-family: inherit;
`

export const TicketPreviewEye = ({ onClick }) => (
    <PreviewEyeButton
        type="button"
        title="Preview ticket"
        aria-label="Preview ticket"
        onClick={onClick}
    >
        <Eye size={14} weight="bold" />
    </PreviewEyeButton>
)

export const TicketQuickPreview = ({
    ticket,
    detail,
    loading = false,
    error = '',
    anchorEl,
    open,
    onClose,
    onView,
}) => {
    const popupRef = useRef(null)
    const [pinned, setPinned] = useState(false)

    const positionPreview = useCallback(() => {
        if (!anchorEl || !popupRef.current) return
        const popup = popupRef.current
        const anchor = anchorEl.getBoundingClientRect()
        const gap = 12
        const pad = 8
        const width = popup.offsetWidth || 420
        const height = popup.offsetHeight || 320
        const fitsRight = anchor.right + gap + width <= window.innerWidth - pad
        let left = fitsRight
            ? anchor.right + gap
            : anchor.left - width - gap
        let top = anchor.top - 8
        top = Math.min(
            Math.max(pad, top),
            window.innerHeight - height - pad,
        )
        left = Math.min(
            Math.max(pad, left),
            window.innerWidth - width - pad,
        )
        popup.style.left = `${left}px`
        popup.style.top = `${top}px`
    }, [anchorEl])

    useEffect(() => {
        if (!open) {
            setPinned(false)
            return undefined
        }
        positionPreview()
        const rafId = requestAnimationFrame(positionPreview)
        const onScroll = () => positionPreview()
        window.addEventListener('scroll', onScroll, true)
        window.addEventListener('resize', onScroll)
        return () => {
            cancelAnimationFrame(rafId)
            window.removeEventListener('scroll', onScroll, true)
            window.removeEventListener('resize', onScroll)
        }
    }, [open, anchorEl, detail, loading, positionPreview])

    useEffect(() => {
        if (!open) return undefined
        const onPointerDown = ({ target }) => {
            if (pinned) return
            if (popupRef.current?.contains(target)) return
            if (anchorEl?.contains(target)) return
            onClose?.()
        }
        document.addEventListener('mousedown', onPointerDown)
        return () => document.removeEventListener('mousedown', onPointerDown)
    }, [open, pinned, anchorEl, onClose])

    if (!open || !ticket) return null

    const source = detail || null
    const statusId = source?.statusId ?? ticket.statusId
    const statusMeta = TICKET_STATUS[statusId] || TICKET_STATUS[1]
    const st = statusStyle(statusId)
    const priorityMeta =
        TICKET_PRIORITY[source?.priorityId] || TICKET_PRIORITY[2]
    const title = source?.title || ticket.title || 'Ticket'
    const owner = source ? formatTicketAssigneeLabel(source) : ''
    const assigneeType =
        source?.assignedToTypeId != null
            ? getAssigneeTypeLabel(source.assignedToTypeId)
            : ''
    const createdBy = source?.createdByFullName || ''
    const completedBy = source?.auditSubmittedByName || ''
    const answer = source?.answerText || ''

    return createPortal(
        <QuickPreview ref={popupRef}>
            <PreviewHeader>
                <div>
                    <h2>
                        Ticket #{ticket.ticketId}
                        {title ? ` — ${title}` : ''}
                    </h2>
                    <div className="sub">
                        {ticket.templateName || source?.auditName || 'Ticket'}
                    </div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                    <SmallButton
                        type="button"
                        $active={pinned}
                        onClick={() => setPinned(value => !value)}
                    >
                        {pinned ? 'Pinned' : 'Pin'}
                    </SmallButton>
                    <SmallButton type="button" onClick={onClose}>
                        ×
                    </SmallButton>
                </div>
            </PreviewHeader>
            <PreviewBody>
                {loading && !source && (
                    <LoadingText>Loading ticket details…</LoadingText>
                )}
                {error && !source && (
                    <LoadingText>{error}</LoadingText>
                )}
                {source && (
                    <>
                        <BadgeRow>
                            <TicketStatusPill $bg={st.bg} $color={st.color}>
                                {statusMeta.label}
                            </TicketStatusPill>
                            <TicketStatusPill
                                $bg="#fef2f2"
                                $color={priorityMeta.color}
                            >
                                {priorityMeta.label}
                            </TicketStatusPill>
                            {source.ticketType && (
                                <TicketStatusPill $bg="#f4f4f5" $color="#52525b">
                                    {source.ticketType}
                                </TicketStatusPill>
                            )}
                        </BadgeRow>
                        <SectionLabel>Audit context</SectionLabel>
                        <MetaGrid>
                            <span className="k">Audit</span>
                            <span className="v">{source.auditName || '—'}</span>
                            <span className="k">Location</span>
                            <span className="v">
                                {formatLocation(
                                    source.locationName,
                                    source.locationAddress,
                                )}
                            </span>
                            <span className="k">Completed</span>
                            <span className="v">
                                {formatTicketDateTime(source.auditCompletedOn)}
                                {completedBy ? ` by ${completedBy}` : ''}
                            </span>
                            <span className="k">Created</span>
                            <span className="v">
                                {formatTicketDateTime(source.createdOn)}
                                {createdBy ? ` by ${createdBy}` : ''}
                            </span>
                        </MetaGrid>
                        <SectionLabel>Ticket details</SectionLabel>
                        <MetaGrid>
                            <span className="k">Question</span>
                            <span className="v">
                                {source.questionText || source.title || '—'}
                            </span>
                            <span className="k">Answer</span>
                            <span className="v">
                                {answer || '—'}
                                {answer && isAnswerNegative(answer) && (
                                    <Chip $danger>NC</Chip>
                                )}
                            </span>
                            <span className="k">Assigned to</span>
                            <span className="v">
                                <span
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6,
                                    }}
                                >
                                    {owner && owner !== '—' && (
                                        <Avatar>{getInitials(owner)}</Avatar>
                                    )}
                                    {owner || '—'}
                                    {assigneeType && assigneeType !== 'Assignee' && (
                                        <Chip>{assigneeType}</Chip>
                                    )}
                                </span>
                            </span>
                        </MetaGrid>
                    </>
                )}
            </PreviewBody>
            <PreviewFooter>
                <ViewButton type="button" onClick={() => onView(ticket.ticketId)}>
                    View ticket
                </ViewButton>
            </PreviewFooter>
        </QuickPreview>,
        document.body,
    )
}
